import { has, isEqual } from 'lodash';

import { entityDefinition } from '@work4all/models/lib/Classes/entityDefinitions';
import { Project } from '@work4all/models/lib/Classes/Project.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';

import { typeNameToSdObjType } from '@work4all/utils';

import { TargetEntityRecordMatcherConfigs } from './types';

type Props<TData> = {
  sourceEntity: Entities;
  data: Record<string, unknown>[];
  config: TargetEntityRecordMatcherConfigs<TData>;
};
export const normalizeSourceEntityRecordMatcherResponseData = <TData>({
  sourceEntity,
  data,
  config,
}: Props<TData>): TData => {
  const isBusinessPartner = [Entities.customer, Entities.supplier].includes(
    sourceEntity
  );
  const searchKeyForSourceEntity = isBusinessPartner
    ? `businessPartner.data.${sourceEntity}`
    : sourceEntity;

  const isSourceEntityInTheFields = has(
    config.fields,
    searchKeyForSourceEntity
  );

  let editedData = [];

  // TODO: WW-7211 Workaround
  if (isSourceEntityInTheFields) {
    data.forEach((item) => {
      let editedItem;
      if (isBusinessPartner) {
        editedItem = {
          ...item,
          businessPartner: {
            id: item.id,
            data: item,
            businessPartnerType:
              sourceEntity === Entities.customer
                ? SdObjType.KUNDE
                : SdObjType.LIEFERANT,
          },
          contact: item.mainContact,
        };
      } else {
        const definitions = entityDefinition[sourceEntity]?.fieldDefinitions;

        const sourceTypeExist = definitions?.[sourceEntity];
        const isReferenceType = has(definitions?.[sourceEntity], 'entity');
        if (sourceTypeExist) {
          if (isReferenceType) {
            editedItem = { ...item, [sourceEntity]: item };
          } else {
            editedItem = item;
          }
        } else {
          editedItem = { ...item, [sourceEntity]: item };
        }
      }

      if (sourceEntity === Entities.project) {
        const project = item as Project;
        const businessPartnerData = project?.customer || project?.supplier;

        if (businessPartnerData) {
          editedItem.businessPartner = {
            data: businessPartnerData,
            businessPartnerType: typeNameToSdObjType(
              businessPartnerData.__typename
            ),
          };
          editedItem.contact = businessPartnerData.mainContact;
        }
      }

      editedData.push(editedItem);
    });
  } else {
    editedData = data;
  }

  const sourceEntitySchemaKeys = Object.keys(editedData?.[0] ?? {});

  const fieldsMapping =
    config?.fieldsMappingBySourceEntity?.[sourceEntity] ?? {};

  const reversedFieldsMapping = Object.fromEntries(
    Object.entries(fieldsMapping).map(([key, value]) => [value, key])
  );

  const result = sourceEntitySchemaKeys.reduce((acc, key) => {
    const values = editedData.map((item) => item[key]);
    const [first, ...rest] = values;
    const isAllValuesEqual = rest.every((item) => isEqual(item, first));

    const finalKey = reversedFieldsMapping[key] ?? key;

    acc[finalKey] = isAllValuesEqual ? first : null;

    return acc;
  }, {});

  return result as TData;
};
